import React from "react";
import { AlertTriangle, Info, X } from "lucide-react";

export default function AlertModal({ 
    isOpen, 
    title, 
    message, 
    onConfirm, 
    onCancel, 
    confirmText = "Confirmer", 
    cancelText = "Annuler",
    type = "danger" // "danger", "warning", "info"
}) {
    if (!isOpen) return null;

    const styles = {
        danger: {
            icon: <AlertTriangle className="h-6 w-6 text-red-600" />,
            iconBg: "bg-red-100",
            buttonBg: "bg-red-600 hover:bg-red-700 focus:ring-red-500"
        },
        warning: {
            icon: <AlertTriangle className="h-6 w-6 text-orange-600" />,
            iconBg: "bg-orange-100",
            buttonBg: "bg-orange-600 hover:bg-orange-700 focus:ring-orange-500"
        },
        info: {
            icon: <Info className="h-6 w-6 text-indigo-600" />,
            iconBg: "bg-indigo-100",
            buttonBg: "bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500"
        }
    };

    const currentStyle = styles[type] || styles.danger;

    return (
        <>
            <style>{`
                @keyframes modalFadeIn {
                    0% { opacity: 0; backdrop-filter: blur(0px); }
                    100% { opacity: 1; backdrop-filter: blur(8px); }
                }
                @keyframes modalPopIn {
                    0% { transform: scale(0.95) translateY(20px); opacity: 0; }
                    100% { transform: scale(1) translateY(0); opacity: 1; }
                }
                @keyframes pulseRing {
                    0% { box-shadow: 0 0 0 0 rgba(currentcolor, 0.4); }
                    70% { box-shadow: 0 0 0 10px rgba(currentcolor, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(currentcolor, 0); }
                }
                .animate-modal-overlay {
                    animation: modalFadeIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                    background-color: rgba(15, 23, 42, 0.6);
                }
                .animate-modal-content {
                    animation: modalPopIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
                }
                .pulse-icon {
                    animation: pulseRing 2s infinite;
                }
            `}</style>
            <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden animate-modal-overlay px-4">
                <div className="relative w-full max-w-md mx-auto animate-modal-content">
                    <div className="relative bg-white/95 backdrop-blur-xl rounded-[2rem] shadow-[0_20px_50px_-12px_rgba(0,0,0,0.25)] border border-white/50 overflow-hidden">
                        
                        {/* Motif de fond décoratif */}
                        <div className="absolute -top-24 -right-24 w-48 h-48 bg-gradient-to-br from-gray-100 to-transparent rounded-full opacity-60 pointer-events-none blur-2xl"></div>
                        <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${type === 'danger' ? 'from-red-400 to-rose-600' : type === 'warning' ? 'from-amber-400 to-orange-500' : 'from-indigo-400 to-blue-600'}`}></div>

                        <button
                            onClick={onCancel}
                            type="button"
                            className="absolute top-5 right-5 text-gray-400 hover:text-gray-800 bg-gray-50/50 hover:bg-gray-100/80 rounded-full p-2.5 transition-all duration-300 z-10 focus:outline-none focus:ring-2 focus:ring-gray-200"
                        >
                            <X className="w-5 h-5" />
                            <span className="sr-only">Fermer</span>
                        </button>

                        <div className="p-8 pb-6">
                            <div className="flex flex-col items-center text-center">
                                <div className={`flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-3xl ${currentStyle.iconBg} shadow-sm mb-6 pulse-icon ring-4 ring-white`}>
                                    {React.cloneElement(currentStyle.icon, { className: "h-10 w-10 " + currentStyle.icon.props.className.split(' ').find(c => c.startsWith('text-')) })}
                                </div>
                                <div className="mt-2 flex-1">
                                    <h3 className="text-2xl font-extrabold text-gray-900 tracking-tight font-sans" id="modal-title">
                                        {title}
                                    </h3>
                                    <div className="mt-4">
                                        <p className="text-base text-gray-500 leading-relaxed font-medium px-2">
                                            {message}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="px-8 pb-8 pt-4 flex flex-col-reverse sm:flex-row gap-3">
                            <button
                                type="button"
                                onClick={onCancel}
                                className="w-full sm:w-1/2 inline-flex justify-center items-center rounded-2xl bg-white px-5 py-3.5 text-sm font-bold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-200 hover:bg-gray-50 hover:text-gray-900 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-gray-200"
                            >
                                {cancelText}
                            </button>
                            <button
                                type="button"
                                onClick={onConfirm}
                                className={`w-full sm:w-1/2 inline-flex justify-center items-center rounded-2xl px-5 py-3.5 text-sm font-bold text-white shadow-lg sm:ml-3 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl ${currentStyle.buttonBg}`}
                            >
                                {confirmText}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
